import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectClients, selectClientsIsLoading } from '@client/model/selectors';
import { useAdvisorOnboarding } from './useAdvisorOnboarding';
import { ERROR_OR_INCOMPLETE_STATUS_TAGS } from '@client';
import { ONBOARDING_V2_CUTOFF_TIMESTAMP } from '@shared';

export function useTrialProgressSync(advisorId: string, advisorCreatedDateTime?: Date) {
  const clients = useSelector(selectClients);
  const clientsIsLoading = useSelector(selectClientsIsLoading);
  const { isStepCompleted, markStepAsCompleted, isLoading: isOnboardingLoading } =
    useAdvisorOnboarding(advisorId);

  const isLoading = clientsIsLoading || isOnboardingLoading;

  const shouldShowQuickStartGuide = useMemo(() => {
    if (isLoading) {
      return false;
    }

    // Only show for advisors created after onboarding v2 cutoff
    const isAfterOnboardingV2Cutoff =
      advisorCreatedDateTime &&
      new Date(advisorCreatedDateTime).getTime() > ONBOARDING_V2_CUTOFF_TIMESTAMP;

    if (!isAfterOnboardingV2Cutoff) {
      // For existing advisors (before cutoff): show trial progress only if they have 0 valid clients
      // (after filtering out clients with error/incomplete status tags)
      const validClients = clients.filter(
        client => !client.clientTags.some(tag => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag)),
      );
      return validClients.length === 0;
    }

    // For new advisors (after cutoff): show if not all steps are complete
    const isAllComplete =
      isStepCompleted('invite-client') &&
      isStepCompleted('client-intake-form') &&
      isStepCompleted('review-results');

    return !isAllComplete;
  }, [isLoading, advisorCreatedDateTime, clients, isStepCompleted]);

  return { isStepCompleted, markStepAsCompleted, shouldShowQuickStartGuide };
}
